/**
 * Import wizard API (W-31c / W-31d) — the six RPCs of migration
 * 20260923000005 plus the two reads the wizard needs around them.
 *
 * Everything that decides anything lives in SQL: the RPCs are `security
 * definer` and re-check `private.master_writer`, so a client that guesses a
 * batch id gets 42501 back. The staging tables are readable (owner, partner,
 * accountant) and writable only through these functions.
 *
 * The raw file never leaves the browser except as the Storage object the audit
 * trail keeps (`imports/<company>/imports/<uuid>-<name>`) and as the already
 * mapped rows of `append_import_rows`.
 */
import type { AslenClient, Json, Tables } from '../db';
import type { ImportKind } from '../constants';
import { chunkRows, IMPORT_CHUNK_SIZE, type StagedRow } from '../imports/mapping';
import { parseImportMessages, parseImportSummary, parseValidateResult, type ImportRowResult, type ImportSummary, type ValidateResult } from '../imports/results';
import { unwrap } from './errors';

export type ImportBatch = Tables<'import_batches'>;
export type ImportRowRecord = Tables<'import_rows'>;

export interface StageImportFile {
  fileName: string;
  filePath?: string | null;
  sha256?: string | null;
  sizeBytes?: number | null;
  format: 'csv' | 'tsv' | 'xlsx';
  templateVersion?: number;
}

/**
 * Creates the batch. `company_id` travels inside `p_file` because that is where
 * `stage_import` reads it from before it checks the role.
 */
export async function stageImport(
  client: AslenClient,
  companyId: string,
  kind: ImportKind,
  file: StageImportFile,
  options: Record<string, Json> = {},
): Promise<string> {
  return unwrap(
    await client.rpc('stage_import', {
      p_kind: kind,
      p_file: {
        company_id: companyId,
        file_name: file.fileName,
        file_path: file.filePath ?? null,
        file_sha256: file.sha256 ?? null,
        file_size_bytes: file.sizeBytes ?? null,
        format: file.format,
        template_version: file.templateVersion ?? 1,
      } as unknown as Json,
      p_options: options as unknown as Json,
    }),
  ) as string;
}

/**
 * Sends the mapped rows in chunks of 500 — the cap `append_import_rows`
 * enforces. `onProgress` is what the footer's "Doğrulanıyor… {{done}} /
 * {{total}}" counts, so the owner sees a 2.000-row file move.
 */
export async function appendImportRows(
  client: AslenClient,
  batchId: string,
  rows: StagedRow[],
  onProgress?: (done: number, total: number) => void,
): Promise<number> {
  let done = 0;
  for (const chunk of chunkRows(rows, IMPORT_CHUNK_SIZE)) {
    unwrap(await client.rpc('append_import_rows', { p_batch: batchId, p_rows: chunk as unknown as Json }));
    done += chunk.length;
    onProgress?.(done, rows.length);
  }
  return done;
}

/** The dry run: per-row status and messages, nothing written to stock. */
export async function validateImport(client: AslenClient, batchId: string, options?: Record<string, Json>): Promise<ValidateResult> {
  const data = unwrap(
    await client.rpc('validate_import', { p_batch: batchId, p_options: (options ?? undefined) as unknown as Json }),
  );
  return parseValidateResult(data);
}

export interface PostImportResult {
  batchId: string;
  status: string;
  summary: ImportSummary;
}

/**
 * One transaction: re-validates under locks, then writes. `skip_errors` is the
 * "Hatalı {{n}} satırı atla" checkbox; without it a single error refuses the
 * whole batch (hint `import_has_errors`).
 */
export async function postImport(
  client: AslenClient,
  batchId: string,
  options: { openingDate?: string; skipErrors?: boolean; updateExisting?: boolean } = {},
): Promise<PostImportResult> {
  const payload: Record<string, Json> = {};
  if (options.openingDate) payload.opening_date = options.openingDate;
  if (options.skipErrors !== undefined) payload.skip_errors = options.skipErrors;
  if (options.updateExisting !== undefined) payload.update_existing = options.updateExisting;
  const data = unwrap(await client.rpc('post_import', { p_batch: batchId, p_options: payload as unknown as Json })) as Record<string, unknown>;
  return {
    batchId: String(data.batch_id ?? batchId),
    status: String(data.status ?? 'posted'),
    summary: parseImportSummary(data.summary),
  };
}

/**
 * Mirror movements through the engine. Refused when a balance would go negative
 * — the engine raises "Geri alınamaz — … stoğu eksiye düşer", which the UI shows
 * as the drawn blocked state instead of a generic failure.
 */
export async function revertImport(client: AslenClient, batchId: string): Promise<ImportSummary> {
  return parseImportSummary(unwrap(await client.rpc('revert_import', { p_batch: batchId })));
}

/** "Vazgeç": the staged rows go, the batch stays in the history as `discarded`. */
export async function discardImport(client: AslenClient, batchId: string): Promise<void> {
  unwrap(await client.rpc('discard_import', { p_batch: batchId }));
}

// ── reads ───────────────────────────────────────────────────────────────────
/** The "Yükleme geçmişi" list; one kind, or all of them for the settings card. */
export async function listImportBatches(client: AslenClient, companyId: string, kind?: ImportKind, limit = 20): Promise<ImportBatch[]> {
  let q = client.from('import_batches').select('*').eq('company_id', companyId).order('created_at', { ascending: false }).limit(limit);
  if (kind) q = q.eq('kind', kind);
  return unwrap(await q);
}

export async function getImportBatch(client: AslenClient, companyId: string, batchId: string): Promise<ImportBatch | null> {
  const rows = unwrap(await client.from('import_batches').select('*').eq('company_id', companyId).eq('id', batchId).limit(1));
  return rows[0] ?? null;
}

/**
 * The staged rows of a batch reopened from the history — `raw` is what the
 * preview table draws (the file's own SKU, warehouse and lot text), which the
 * `validate_import` payload does not repeat.
 */
export async function listImportRows(client: AslenClient, batchId: string, limit = 2000): Promise<ImportRowResult[]> {
  const rows = unwrap(
    await client
      .from('import_rows')
      .select('row_no, status, messages, parsed, snapshot, raw, target_id')
      .eq('batch_id', batchId)
      .order('row_no')
      .limit(limit),
  );
  return rows.map((r) => ({
    row_no: r.row_no,
    status: r.status,
    messages: parseImportMessages(r.messages),
    parsed: (r.parsed ?? null) as ImportRowResult['parsed'],
    snapshot: (r.snapshot ?? null) as ImportRowResult['snapshot'],
    raw: (r.raw ?? null) as Record<string, string> | null,
    target_id: r.target_id,
  }));
}

/**
 * "Hareket no" of step 4, keyed by the count line each row points at
 * (`import_rows.target_id`). Every movement of one posting carries the same
 * `stock_movements.batch_id`, which `post_opening_stock` returns in its summary —
 * so one short request brings back the numbers for a 2.000-row file instead of a
 * URL full of row ids.
 */
export async function listImportMovementNos(client: AslenClient, companyId: string, movementBatchId: string): Promise<Record<string, string>> {
  const rows = unwrap(
    await client
      .from('stock_movements')
      .select('no, ref_line_id')
      .eq('company_id', companyId)
      .eq('batch_id', movementBatchId)
      .not('ref_line_id', 'is', null),
  );
  const byLine: Record<string, string> = {};
  for (const r of rows) if (r.ref_line_id) byLine[r.ref_line_id] = r.no;
  return byLine;
}

/**
 * What the "Başlamadan önce" checklist and the "Değer" column need about this
 * company's items, and no view provides: which items are lot-tracked (a blank
 * lot cell becomes a generated number), which have no conversion for their own
 * stock unit (a quantity in that unit cannot be converted) and which have no
 * standard cost (such a row books 0 ₺ unless the file carries a cost).
 *
 * Service items are left out — they hold no stock, so the import never touches
 * them. `item_costs` is the same source `validate_opening_stock` reads for its
 * `cost_missing` warning, so the screen and the server agree.
 */
export interface ImportItemFacts {
  /** items that can hold stock */
  stocked: number;
  /** their ids, where lot tracking is on */
  lotTracked: string[];
  /** no `item_uoms` row for the item's stock unit */
  missingStockUnit: number;
  /** no `item_costs.standard_cost_base` */
  missingStandardCost: number;
  /** item id → standard cost per base unit, in the company's base currency */
  standardCost: Record<string, number>;
}

export async function getImportItemFacts(client: AslenClient, companyId: string): Promise<ImportItemFacts> {
  const [itemRes, uomRes, costRes] = await Promise.all([
    client.from('items').select('id, stock_unit, is_lot_tracked').eq('company_id', companyId).is('deleted_at', null).neq('type', 'service'),
    client.from('item_uoms').select('item_id, unit').eq('company_id', companyId),
    client.from('item_costs').select('item_id, standard_cost_base').eq('company_id', companyId).not('standard_cost_base', 'is', null),
  ]);
  const items = unwrap(itemRes);
  const uoms = unwrap(uomRes);
  const costs = unwrap(costRes);
  const hasUom = new Set(uoms.map((u) => `${u.item_id}|${u.unit}`));
  const standardCost: Record<string, number> = {};
  for (const c of costs) if (c.standard_cost_base != null) standardCost[c.item_id] = c.standard_cost_base;
  return {
    stocked: items.length,
    lotTracked: items.filter((i) => i.is_lot_tracked).map((i) => i.id),
    missingStockUnit: items.filter((i) => !hasUom.has(`${i.id}|${i.stock_unit}`)).length,
    missingStandardCost: items.filter((i) => standardCost[i.id] == null).length,
    standardCost,
  };
}

/**
 * Has this exact file been through the wizard before? Same SHA-256 on a posted
 * batch is what the warning "Bu dosya … zaten işlendi" reports; re-posting is
 * allowed (only differences are written) but the owner should know.
 */
export async function findImportByHash(client: AslenClient, companyId: string, sha256: string): Promise<ImportBatch | null> {
  const rows = unwrap(
    await client
      .from('import_batches')
      .select('*')
      .eq('company_id', companyId)
      .eq('file_sha256', sha256)
      .eq('status', 'posted')
      .order('posted_at', { ascending: false })
      .limit(1),
  );
  return rows[0] ?? null;
}
